import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { MobileNavigation } from '../components/MobileNavigation';
import { CurrencyDollar, Plus, Trash, ArrowLeft, User, Clock, Warning, CheckCircle, MagnifyingGlass } from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { getApiBaseUrl } from '../utils/api';
const CREDIT_REASONS = [
  { value: 'parent_cancellation', label: 'Parent Cancellation' },
  { value: 'program_cancellation', label: 'Program Cancellation' },
  { value: 'courtesy', label: 'Courtesy Credit' },
  { value: 'special_circumstance', label: 'Special Circumstance' }
];

export function AdminCredits() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [creditsData, setCreditsData] = useState({ parents: [], stats: {} });
  const [parents, setParents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedParent, setSelectedParent] = useState(null);
  const [parentCredits, setParentCredits] = useState(null);
  
  // Issue Credit Modal
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [issueForm, setIssueForm] = useState({
    parent_id: '',
    amount: '',
    reason: '',
    reason_notes: '',
    expires_in_days: 90
  });
  const [issuing, setIssuing] = useState(false);
  
  useEffect(() => {
    loadCreditsData();
    loadParents();
  }, []);
  
  const loadCreditsData = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/credits`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCreditsData(data);
      }
    } catch (error) {
      console.error('Load credits error:', error);
      toast.error('Failed to load credits data');
    } finally {
      setLoading(false);
    }
  };
  
  const loadParents = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/parents`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setParents(data);
      }
    } catch (error) {
      console.error('Load parents error:', error);
    }
  };
  
  const loadParentCredits = async (parentId) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/credits/${parentId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setParentCredits(data);
        setSelectedParent(parentId);
      }
    } catch (error) {
      console.error('Load parent credits error:', error);
      toast.error('Failed to load parent credits');
    }
  };
  
  const handleIssueCredit = async () => {
    if (!issueForm.parent_id || !issueForm.amount || !issueForm.reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setIssuing(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/credits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...issueForm,
          amount: parseFloat(issueForm.amount)
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`$${issueForm.amount} credit issued to ${data.parent_name}`);
        setShowIssueModal(false);
        setIssueForm({ parent_id: '', amount: '', reason: '', reason_notes: '', expires_in_days: 90 });
        loadCreditsData();
        loadParents();
        if (selectedParent === issueForm.parent_id) {
          loadParentCredits(issueForm.parent_id);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to issue credit');
      }
    } catch (error) {
      console.error('Issue credit error:', error);
      toast.error('Failed to issue credit');
    } finally {
      setIssuing(false);
    }
  };
  
  const handleRevokeCredit = async (creditId) => {
    if (!window.confirm('Are you sure you want to revoke this credit?')) return;
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/credits/${creditId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Credit revoked successfully');
        loadCreditsData();
        loadParents();
        if (selectedParent) {
          loadParentCredits(selectedParent);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to revoke credit');
      }
    } catch (error) {
      console.error('Revoke credit error:', error);
      toast.error('Failed to revoke credit');
    }
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  const filteredParents = creditsData.parents.filter(p => 
    p.parent_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.parent_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="xl" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pb-20" data-testid="admin-credits-page">
      {/* Header */}
      <div className="glass-effect border-b border-border sticky top-0 z-40">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-xl font-heading">ACCOUNT CREDITS</h1>
                <p className="text-sm text-muted-foreground">
                  Manage parent account credits
                </p>
              </div>
            </div>
            <Button 
              onClick={() => setShowIssueModal(true)}
              className="btn-primary"
              data-testid="issue-credit-btn"
            >
              <Plus size={16} className="mr-1" /> Issue Credit
            </Button>
          </div>
        </div>
      </div>
      
      {/* Stats Overview */}
      <div className="px-4 py-4">
        <div className="grid grid-cols-3 gap-3">
          <Card className="bg-card border-border">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-green-500">
                {formatCurrency(creditsData.stats.total_issued)}
              </p>
              <p className="text-xs text-muted-foreground">Total Issued</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-primary">
                {formatCurrency(creditsData.stats.total_remaining)}
              </p>
              <p className="text-xs text-muted-foreground">Outstanding</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-blue-500">
                {formatCurrency(creditsData.stats.total_used)}
              </p>
              <p className="text-xs text-muted-foreground">Used</p>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Parent Credits List */}
      <div className="px-4">
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Parents with Credits</CardTitle>
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 h-8 w-40"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {filteredParents.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <CurrencyDollar size={40} className="mx-auto mb-2 opacity-50" />
                <p>No credits issued yet</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filteredParents.map((parent) => (
                  <div 
                    key={parent.parent_id}
                    className="p-3 hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => loadParentCredits(parent.parent_id)}
                    data-testid={`parent-row-${parent.parent_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                          <User size={20} className="text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{parent.parent_name}</p>
                          <p className="text-xs text-muted-foreground">{parent.parent_email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-500">
                          {formatCurrency(parent.total_remaining)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {parent.credits_count} credit{parent.credits_count !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Parent Detail Panel */}
      {parentCredits && (
        <div className="px-4 mt-4">
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">{parentCredits.parent?.name}</CardTitle>
                  <p className="text-sm text-muted-foreground">{parentCredits.parent?.email}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Available Balance</p>
                  <p className="text-2xl font-bold text-green-500">
                    {formatCurrency(parentCredits.total_balance)}
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <h4 className="font-medium mb-2">Credit History</h4>
              {parentCredits.credits.length === 0 ? (
                <p className="text-muted-foreground text-sm">No credits</p>
              ) : (
                <div className="space-y-2">
                  {parentCredits.credits.map((credit) => (
                    <div 
                      key={credit.credit_id}
                      className={`p-3 rounded-lg border ${credit.revoked ? 'bg-red-500/10 border-red-500/30' : 'bg-muted/30 border-border'}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className={`font-bold ${credit.revoked ? 'text-red-500 line-through' : ''}`}>
                            {formatCurrency(credit.amount)}
                          </span>
                          {credit.revoked && (
                            <span className="text-xs bg-red-500/20 text-red-500 px-2 py-0.5 rounded">
                              REVOKED
                            </span>
                          )}
                          {!credit.revoked && credit.remaining_balance < credit.amount && (
                            <span className="text-xs bg-blue-500/20 text-blue-500 px-2 py-0.5 rounded">
                              {formatCurrency(credit.remaining_balance)} remaining
                            </span>
                          )}
                        </div>
                        {!credit.revoked && credit.remaining_balance > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 h-7"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRevokeCredit(credit.credit_id);
                            }}
                            data-testid={`revoke-btn-${credit.credit_id}`}
                          >
                            <Trash size={14} />
                          </Button>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground space-y-1">
                        <div className="flex justify-between">
                          <span>Reason: {CREDIT_REASONS.find(r => r.value === credit.reason)?.label || credit.reason}</span>
                          <span>Issued: {formatDate(credit.issued_at)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>By: {credit.created_by_admin_name || 'System'}</span>
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            Expires: {formatDate(credit.expires_at)}
                          </span>
                        </div>
                        {credit.reason_notes && (
                          <p className="italic">Note: {credit.reason_notes}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {parentCredits.usages && parentCredits.usages.length > 0 && (
                <>
                  <h4 className="font-medium mb-2 mt-4">Usage History</h4>
                  <div className="space-y-2">
                    {parentCredits.usages.map((usage) => (
                      <div key={usage.usage_id} className="p-2 bg-muted/20 rounded text-sm flex justify-between">
                        <span>Used {formatCurrency(usage.amount_used)}</span>
                        <span className="text-muted-foreground">{formatDate(usage.used_at)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Issue Credit Modal */}
      <Dialog open={showIssueModal} onOpenChange={setShowIssueModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Issue Account Credit</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Parent *</Label>
              <Select
                value={issueForm.parent_id}
                onValueChange={(value) => setIssueForm({...issueForm, parent_id: value})}
              >
                <SelectTrigger data-testid="parent-select">
                  <SelectValue placeholder="Select parent..." />
                </SelectTrigger>
                <SelectContent>
                  {parents.map((parent) => (
                    <SelectItem key={parent.user_id} value={parent.user_id}>
                      {parent.name} ({parent.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Amount ($) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                placeholder="15.00"
                value={issueForm.amount}
                onChange={(e) => setIssueForm({...issueForm, amount: e.target.value})}
                data-testid="credit-amount-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Reason *</Label>
              <Select
                value={issueForm.reason}
                onValueChange={(value) => setIssueForm({...issueForm, reason: value})}
              >
                <SelectTrigger data-testid="reason-select">
                  <SelectValue placeholder="Select reason..." />
                </SelectTrigger>
                <SelectContent>
                  {CREDIT_REASONS.map((reason) => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Expiration (days)</Label>
              <Input
                type="number"
                min="1"
                max="365"
                value={issueForm.expires_in_days}
                onChange={(e) => setIssueForm({...issueForm, expires_in_days: parseInt(e.target.value) || 90})}
                data-testid="expiration-input"
              />
              <p className="text-xs text-muted-foreground">Default: 90 days</p>
            </div>
            
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Additional notes..."
                value={issueForm.reason_notes}
                onChange={(e) => setIssueForm({...issueForm, reason_notes: e.target.value})}
                data-testid="notes-textarea"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIssueModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleIssueCredit} 
              disabled={issuing}
              className="btn-primary"
              data-testid="confirm-issue-btn"
            >
              {issuing ? <LoadingSpinner size="sm" className="mr-2" /> : null}
              Issue Credit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <MobileNavigation userRole="admin" />
    </div>
  );
}
